"use client";
import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";

import {
  deleteActorEmail,
  deleteToken,
  storeActorEmail,
  storeToken,
} from "../storage/AuthStorage";

const UserContext = createContext({
  isLoading: true,
  user: {},
  isAuthenticated: false,
  isAuthorized: false,
  permissions: [],
  loginWithRedirect: () => {},
  hasOwnerPermission: () => {},
  getAccessTokenSilently: () => {},
  logout: () => {},
});

export const UserContextProvider = ({ children }) => {
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);

  const hasOwnerPermission = useCallback(async () => {
    setIsCheckingPermissions(true);
    try {
      const token = await getAccessTokenSilently();
      const decoded = jwtDecode(token);

      const userPermissions = decoded?.permissions ?? [];
      setPermissions(userPermissions);

      const hasPermission =
        userPermissions.length > 0 && userPermissions.includes("admin");

      setIsAuthorized(hasPermission);

      return hasPermission || false;
    } catch (error) {
      console.error("Error decoding token or checking permissions:", error);
      setPermissions([]);
      setIsAuthorized(false);

      return false;
    } finally {
      setIsCheckingPermissions(false);
    }
  }, [
    getAccessTokenSilently,
    setIsAuthorized,
    setPermissions,
    setIsCheckingPermissions,
  ]);

  const storeTokenInLocalStorage = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      storeToken(token);
      // The access token carries permissions but no address, so keep the one the ID token gave us:
      // it is what the notification audit trail shows instead of a bare auth0 subject.
      storeActorEmail(user?.email ?? null);
    } catch (error) {
      // A session that has expired still leaves isAuthenticated true, so this runs and rejects.
      // Unhandled, that reached the browser as a runtime error overlay reading "Login required" —
      // which tells someone nothing about what to do. Send them to sign in instead.
      deleteToken();
      deleteActorEmail();
      if (error?.error === "login_required" || error?.error === "consent_required") {
        loginWithRedirect();
        return;
      }
      console.error("Could not renew the session", error);
    }
  }, [getAccessTokenSilently, loginWithRedirect, user]);

  const handleLogout = useCallback(async () => {
    deleteToken();
    deleteActorEmail();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  useEffect(() => {
    if (isAuthenticated) {
      hasOwnerPermission();
      storeTokenInLocalStorage();
    }
  }, [hasOwnerPermission, isAuthenticated, storeTokenInLocalStorage]);

  return (
    <UserContext.Provider
      value={{
        isAuthenticated,
        isAuthorized,
        permissions,
        isLoading: isLoading || isCheckingPermissions, // both must be finished befre loading is considered complete
        loginWithRedirect,
        user,
        hasOwnerPermission,
        getAccessTokenSilently,
        logout: handleLogout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(
      "useUserContext must be used within an UserContextProvider"
    );
  }
  return context;
};
